import { Filter, RefreshCw, Route } from 'lucide-react';

import type { RouteMode, RouteWeights } from '@/features/network/models';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { shorten } from '@/lib/format';

export type FiltersState = {
  source: string;
  target: string;
  mode: RouteMode;
  maxHops: number;
  minCapacityMbps: number;
  includeUnknownLatency: boolean;
  amortizationMonths: number;
  topK: number;
  statusSelection: string[];
  serviceTypeSelection: string[];
  searchQuery: string;
  maxTotalLatencyMs: number | null;
  maxEffectiveMonthlyCost: number | null;
  avoidLocationsText: string;
  orderedWaypointsText: string;
  routeWeights: RouteWeights;
};

export function FiltersPanel({
  locations,
  visibleLocations,
  statuses,
  serviceTypes,
  state,
  onStateChange,
  onReset,
  onRerunLayout,
  showRouteControls = true,
  showDatasetFilters = true,
}: {
  locations: string[];
  visibleLocations: string[];
  statuses: string[];
  serviceTypes: string[];
  state: FiltersState;
  onStateChange: (patch: Partial<FiltersState>) => void;
  onReset: () => void;
  onRerunLayout?: () => void;
  showRouteControls?: boolean;
  showDatasetFilters?: boolean;
}) {
  function toggle(list: string[], value: string) {
    return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
  }

  function weightPatch(key: keyof RouteWeights, value: number) {
    onStateChange({ routeWeights: { ...state.routeWeights, [key]: Math.max(0, value) } });
  }

  const title = !showRouteControls && showDatasetFilters ? 'Фильтр' : 'Фильтры и ограничения';
  const subtitle = !showRouteControls && showDatasetFilters ? 'Выбери Status и Service Type для отображения графа и карты.' : 'Сначала ограничь граф, потом ищи путь.';

  return (
    <Card className="sticky top-4 p-6">
      <div className="flex items-center gap-2 text-lg font-semibold">
        <Filter className="h-5 w-5" />
        {title}
      </div>
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>

      <div className="mt-6 space-y-5">
        {showRouteControls ? (
          <>
            <div className="grid gap-4">
              <div>
                <label className="mb-2 block text-sm font-medium">Откуда</label>
                <Input list="locations-list" value={state.source} onChange={(event) => onStateChange({ source: event.target.value })} placeholder="Location A" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">Куда</label>
                <Input list="locations-list" value={state.target} onChange={(event) => onStateChange({ target: event.target.value })} placeholder="Location Z" />
              </div>
              <datalist id="locations-list">
                {locations.map((location) => (
                  <option key={location} value={location} />
                ))}
              </datalist>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium">Max hops</label>
                  <Input type="number" min={1} max={10} value={state.maxHops} onChange={(event) => onStateChange({ maxHops: Math.max(1, Number(event.target.value) || 1) })} />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Min capacity (Mbps)</label>
                  <Input type="number" min={0} value={state.minCapacityMbps} onChange={(event) => onStateChange({ minCapacityMbps: Math.max(0, Number(event.target.value) || 0) })} />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Max total latency (ms)</label>
                  <Input type="number" min={0} value={state.maxTotalLatencyMs ?? ''} onChange={(event) => onStateChange({ maxTotalLatencyMs: event.target.value === '' ? null : Math.max(0, Number(event.target.value) || 0) })} placeholder="Без ограничения" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Max effective monthly cost</label>
                  <Input type="number" min={0} value={state.maxEffectiveMonthlyCost ?? ''} onChange={(event) => onStateChange({ maxEffectiveMonthlyCost: event.target.value === '' ? null : Math.max(0, Number(event.target.value) || 0) })} placeholder="Без ограничения" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">NRC amortization (months)</label>
                  <Input type="number" min={1} value={state.amortizationMonths} onChange={(event) => onStateChange({ amortizationMonths: Math.max(1, Number(event.target.value) || 1) })} />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Top routes</label>
                  <Input type="number" min={1} max={20} value={state.topK} onChange={(event) => onStateChange({ topK: Math.max(1, Math.min(20, Number(event.target.value) || 1)) })} />
                </div>
              </div>
              <label className="flex items-center gap-3 rounded-2xl border border-slate-200 p-3 text-sm dark:border-slate-800">
                <Checkbox checked={state.includeUnknownLatency} onChange={(event) => onStateChange({ includeUnknownLatency: event.target.checked })} />
                Разрешать рёбра без latency
              </label>
            </div>

            <div className="grid gap-4">
              <div>
                <label className="mb-2 block text-sm font-medium">Обязательные промежуточные точки</label>
                <Input value={state.orderedWaypointsText} onChange={(event) => onStateChange({ orderedWaypointsText: event.target.value })} placeholder="Через запятую, в нужном порядке" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">Избегать локаций</label>
                <Input value={state.avoidLocationsText} onChange={(event) => onStateChange({ avoidLocationsText: event.target.value })} placeholder="Через запятую" />
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 p-4 dark:border-slate-800">
              <div className="text-sm font-medium">Веса balanced-режима</div>
              <div className="mt-4 space-y-4 text-sm">
                <label className="block">
                  <div className="mb-2">Latency: {state.routeWeights.latency}</div>
                  <input type="range" min={0} max={100} value={state.routeWeights.latency} onChange={(event) => weightPatch('latency', Number(event.target.value))} className="w-full" />
                </label>
                <label className="block">
                  <div className="mb-2">Cost: {state.routeWeights.cost}</div>
                  <input type="range" min={0} max={100} value={state.routeWeights.cost} onChange={(event) => weightPatch('cost', Number(event.target.value))} className="w-full" />
                </label>
                <label className="block">
                  <div className="mb-2">Hops: {state.routeWeights.hops}</div>
                  <input type="range" min={0} max={100} value={state.routeWeights.hops} onChange={(event) => weightPatch('hops', Number(event.target.value))} className="w-full" />
                </label>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">Быстрые подсказки по локациям</label>
              <Input value={state.searchQuery} onChange={(event) => onStateChange({ searchQuery: event.target.value })} placeholder="Искать локации" />
              <div className="mt-3 flex max-h-32 flex-wrap gap-2 overflow-auto rounded-2xl border border-slate-200 p-3 dark:border-slate-800">
                {visibleLocations.slice(0, 20).map((location) => (
                  <button
                    key={location}
                    type="button"
                    onClick={() => (!state.source ? onStateChange({ source: location }) : onStateChange({ target: location }))}
                    className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-700 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200"
                  >
                    {shorten(location, 34)}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : null}

        {showDatasetFilters ? (
          <>
            <div>
              <div className="mb-2 text-sm font-medium">Статусы</div>
              <div className="max-h-44 space-y-2 overflow-auto rounded-2xl border border-slate-200 p-3 dark:border-slate-800">
                {statuses.map((status) => (
                  <label key={status} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-2 text-sm dark:border-slate-800">
                    <span>{status}</span>
                    <Checkbox checked={state.statusSelection.includes(status)} onChange={() => onStateChange({ statusSelection: toggle(state.statusSelection, status) })} />
                  </label>
                ))}
                {statuses.length === 0 ? <div className="text-sm text-slate-500">Нет статусов.</div> : null}
              </div>
              <div className="mt-1 text-xs text-slate-500">Если ничего не отмечено — используются все статусы.</div>
            </div>

            <div>
              <div className="mb-2 text-sm font-medium">Service Type</div>
              <div className="max-h-44 space-y-2 overflow-auto rounded-2xl border border-slate-200 p-3 dark:border-slate-800">
                {serviceTypes.map((serviceType) => (
                  <label key={serviceType} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-2 text-sm dark:border-slate-800">
                    <span>{serviceType}</span>
                    <Checkbox
                      checked={state.serviceTypeSelection.includes(serviceType)}
                      onChange={() => onStateChange({ serviceTypeSelection: toggle(state.serviceTypeSelection, serviceType) })}
                    />
                  </label>
                ))}
                {serviceTypes.length === 0 ? <div className="text-sm text-slate-500">Нет значений Service Type.</div> : null}
              </div>
              <div className="mt-1 text-xs text-slate-500">Если ничего не отмечено — используются все Service Type.</div>
            </div>
          </>
        ) : null}
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <Button type="button" variant="secondary" onClick={onReset}>
          <RefreshCw className="h-4 w-4" />
          Сбросить
        </Button>
        {onRerunLayout ? (
          <Button type="button" variant="secondary" onClick={onRerunLayout}>
            <RefreshCw className="h-4 w-4" />
            Перерисовать
          </Button>
        ) : null}
        <Button
          type="button"
          variant="secondary"
          onClick={() => onStateChange({ source: state.target, target: state.source })}
          disabled={(!state.source && !state.target) || !showRouteControls}
        >
          <Route className="h-4 w-4" />
          Поменять местами
        </Button>
      </div>
    </Card>
  );
}
